import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Compass,
  Sparkles,
  Zap,
  Search,
  Heart,
  Users2,
  Check,
  ChevronRight,
} from "lucide-react";
import { GLA_ASSESSMENT_URL, BOOK_CALL_URL } from "@/lib/constants";

/* ------------------------------------------------------------------ */
/* Six archetypes — the heart of the page                              */
/* ------------------------------------------------------------------ */

const archetypes = [
  {
    name: "Visionary",
    icon: Compass,
    driver: "Possibility",
    question: "What is possible?",
    strengths: "Sees opportunities before others do · Thinks strategically and long-term · Inspires innovation",
    style: "Future-focused · Opportunity-driven · Comfortable with uncertainty",
    watchFor: "Chasing too many ideas · Missing practical details · Overestimating resources",
  },
  {
    name: "Transformational",
    icon: Sparkles,
    driver: "Change",
    question: "What could this become?",
    strengths: "Drives growth and change · Challenges the status quo · Helps others reach their potential",
    style: "Change-focused · Growth-oriented · Seeks meaningful impact",
    watchFor: "Changing direction too often · Disrupting stable systems · Impatience with gradual progress",
  },
  {
    name: "Action-Oriented",
    icon: Zap,
    driver: "Momentum",
    question: "What can we do right now?",
    strengths: "Executes quickly · Creates momentum · Solves problems in real time",
    style: "Fast-moving · Results-focused · Learns by doing",
    watchFor: "Acting before planning · Missing important information · Creating rework",
  },
  {
    name: "Analytical",
    icon: Search,
    driver: "Evidence",
    question: "What do the facts tell us?",
    strengths: "Uses logic and evidence · Identifies risks and patterns · Creates structure and clarity",
    style: "Data-driven · Methodical · Evidence-based",
    watchFor: "Overthinking · Waiting for perfect information · Analysis paralysis",
  },
  {
    name: "Empathetic",
    icon: Heart,
    driver: "People",
    question: "How will this affect people?",
    strengths: "Builds trust and relationships · Creates psychological safety · Understands motivations",
    style: "People-centered · Values harmony and inclusion",
    watchFor: "Avoiding difficult conversations · Delaying necessary decisions · Putting feelings over outcomes",
  },
  {
    name: "Collaborative",
    icon: Users2,
    driver: "Alignment",
    question: "Who should be involved?",
    strengths: "Builds strong teams · Draws out diverse perspectives · Creates buy-in",
    style: "Consensus-seeking · Inclusive · Relationship-focused",
    watchFor: "Too many opinions · Slow decision cycles · Difficulty making unpopular calls",
  },
];

const allies = [
  {
    pair: "Visionary + Transformational",
    tag: "Big ideas, bold change.",
    body: "Natural agreement on possibility and reinvention. Can skip the details.",
  },
  {
    pair: "Analytical + Action-Oriented",
    tag: "Plan it, then ship it.",
    body: "Rigor and momentum — a powerful pairing. Can miss the people.",
  },
  {
    pair: "Empathetic + Collaborative",
    tag: "Trust and buy-in.",
    body: "Deep alignment and care for the team. Can delay the hard call.",
  },
];

const tensions = [
  { pair: "Visionary ↔ Analytical", line: "Possibility meets proof." },
  { pair: "Action-Oriented ↔ Collaborative", line: "Speed meets buy-in." },
  { pair: "Transformational ↔ Empathetic", line: "Change meets care." },
];

const audiences = [
  {
    title: "Individual leaders",
    points: [
      "Understand why you decide the way you do",
      "Recognize your blind spots before they cost you",
      "Make confident decisions faster, without skipping what matters",
    ],
  },
  {
    title: "Leadership teams",
    points: [
      "Understand why some conversations go smoothly and others stall",
      "Turn friction into a strength by valuing different perspectives",
      "Build the right room for each decision",
    ],
  },
  {
    title: "Schools & organizations",
    points: [
      "See your organization's collective decision pattern",
      "Make better calls on AI adoption, college readiness, staffing and resource allocation",
      "Build a shared decision-making language across every team",
    ],
  },
];

const experiences = [
  {
    tag: "Self-guided",
    title: "Individual Genius Leader Archetype™",
    body: "Take the assessment and get your personal results — your primary archetype, strengths, blind spots and stretch perspective.",
    cta: "Take the Genius Leader Archetype™",
    href: GLA_ASSESSMENT_URL,
    external: true,
  },
  {
    tag: "60-minute live",
    title: "Interactive Workshop",
    body: "Teams take the Genius Leader Archetype™, then solve real problems in same-archetype and mixed groups and see the difference for themselves.",
    cta: "Book a workshop",
    href: BOOK_CALL_URL,
    external: true,
  },
  {
    tag: "Campus-wide",
    title: "GLAD Score™ Activation",
    body: "A team- or organization-wide assessment, a decision-pattern analysis and a live debrief with Dr. Crystal.",
    cta: "Book a conversation",
    href: BOOK_CALL_URL,
    external: true,
  },
  {
    tag: "Signature talk",
    title: "The Keynote",
    body: "\"Embrace Your Educational Leadership Genius to Make Great Decisions Faster\" — plus related talks for boards and faculty PD.",
    cta: "Explore speaking",
    href: "/speaking",
    external: false,
  },
];

const faqs = [
  { q: "Is the Genius Leader Archetype a personality test?", a: "No. It identifies your decision drivers — what moves you toward a decision. It doesn't label who you are." },
  { q: "Am I stuck with one archetype?", a: "No. Everyone can access all six. The Genius Leader Archetype shows where you naturally start." },
  { q: "Is there a \"best\" archetype?", a: "No. Every archetype brings a strength and a blind spot. Strong decisions draw on several." },
  { q: "How long does it take?", a: "About 6 minutes." },
  { q: "What's the difference between the Genius Leader Archetype and the GLAD Score™?", a: "The Genius Leader Archetype is about you. The GLAD Score™ is about your team." },
  { q: "Can I use it with my staff or leadership team?", a: "Yes — that's exactly what the GLAD Score™ is for. Book a call to discuss your team." },
  { q: "Who created it?", a: "Dr. Crystal Gifford, CEO of EdgeTech Global. Two decades in finance, economics, curriculum design and leadership development. Author of The Money Shot and The Money Makeover." },
];

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function GLA() {
  return (
    <div data-testid="page-gla">
      {/* 1. HERO */}
      <section className="relative bg-[#1B3654] text-[#FDFBF7] overflow-hidden">
        {/* Decorative hexagon backdrop */}
        <div aria-hidden className="absolute inset-0 opacity-[0.07] pointer-events-none">
          <svg viewBox="0 0 800 600" className="w-full h-full">
            <defs>
              <pattern id="hex" width="60" height="52" patternUnits="userSpaceOnUse" patternTransform="scale(1.5)">
                <polygon points="30,2 56,17 56,45 30,60 4,45 4,17" fill="none" stroke="#B8A26E" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="800" height="600" fill="url(#hex)" />
          </svg>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-28 pb-24 md:pt-40 md:pb-32">
          <div className="max-w-3xl">
            <div className="overline text-[#B8A26E]">The Genius Leader Archetype™</div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.02] mt-4">
              Discover how you make decisions — <em className="italic text-[#E0AC29]">and who you need beside you.</em>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#FDFBF7]/90 leading-relaxed max-w-2xl">
              The <strong className="font-semibold">Genius Leader Archetype™</strong> shows you what naturally moves you toward "yes." The <strong className="font-semibold">GLAD Score™</strong> shows how your whole team decides together.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href={GLA_ASSESSMENT_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="gla-hero-take"
                className="btn-primary bg-[#E0AC29] border-[#E0AC29] text-[#1B3654] hover:bg-[#c9991f]"
              >
                Take the Genius Leader Archetype™ <ArrowUpRight size={16} />
              </a>
              <a
                href={BOOK_CALL_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="gla-hero-book"
                className="btn-secondary !text-[#FDFBF7] !border-[#FDFBF7]/60 hover:!bg-white/10"
              >
                Book a Call About Your Team
              </a>
            </div>
            <p className="mt-8 text-sm text-[#FDFBF7]/70 max-w-xl">
              Created by Dr. Crystal Gifford, decision strategist and CEO of EdgeTech Global. Make great decisions faster.
            </p>
          </div>
        </div>
      </section>

      {/* 2. WHAT THE GLA IS */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <div className="overline text-[#0F4C5C]">What the Genius Leader Archetype is</div>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mt-4">
                Not a personality test. <em className="italic text-[#1B3654]">A decision-driver assessment.</em>
              </h2>
            </div>
            <div className="md:col-span-7 text-lg leading-relaxed text-[#22223B]/80 space-y-5">
              <p>
                The Genius Leader Archetype identifies the perspectives, information and conditions that tend to move you toward a decision.
              </p>
              <p>
                Capable leaders can look at the same situation and legitimately need different things before they feel ready to act. Some need evidence. Some need to see the possibility. Some need to know how people will be affected. <strong className="text-[#1B3654]">The Genius Leader Archetype makes those differences visible.</strong>
              </p>
            </div>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {[
              "Everyone can access all six archetypes. The Genius Leader Archetype shows which one you reach for first and most strongly.",
              "Your archetype is where you start — not where every decision should end.",
              "Every strength has a blind spot. Knowing yours tells you which perspectives to bring in on purpose.",
            ].map((line, i) => (
              <div key={i} className="p-8 border-t-2 border-[#B8A26E]">
                <div className="font-serif-display italic text-4xl text-[#B8A26E]">0{i + 1}</div>
                <p className="mt-6 text-[17px] leading-relaxed text-[#22223B]/85">{line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. SIX ARCHETYPES */}
      <section className="section-pad bg-[#F0EDE6]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="overline text-[#0F4C5C]">The six archetypes</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mt-4">
              Six ways leaders move <em className="italic text-[#1B3654]">toward yes.</em>
            </h2>
          </div>

          {/* Central hexagon motif with archetypes ringed around */}
          <div className="relative mt-16">
            {/* Central "Decision" mark */}
            <div className="flex justify-center mb-10">
              <div
                className="w-32 h-32 flex items-center justify-center bg-[#1B3654] text-[#FDFBF7] shadow-lg"
                style={{ clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)" }}
              >
                <div className="text-center">
                  <div className="overline text-[#B8A26E] text-[10px]">Center</div>
                  <div className="font-serif-display italic text-xl mt-1">Decision</div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {archetypes.map((a) => (
                <div key={a.name} data-testid={`gla-archetype-${a.name.toLowerCase()}`} className="card-soft p-8 relative">
                  <div className="flex items-start justify-between">
                    <a.icon size={28} strokeWidth={1.4} className="text-[#1B3654]" />
                    <div className="text-right">
                      <div className="overline text-[#B8A26E]">Driver</div>
                      <div className="font-serif-display italic text-lg text-[#1B3654]">{a.driver}</div>
                    </div>
                  </div>
                  <h3 className="font-serif-display text-2xl md:text-3xl mt-5">{a.name}</h3>
                  <div className="mt-4 pb-4 border-b border-black/5">
                    <div className="overline text-[#0F4C5C] text-[10px]">Core question</div>
                    <div className="font-serif-display italic text-lg mt-1 text-[#22223B]">"{a.question}"</div>
                  </div>
                  <div className="mt-4 space-y-3 text-sm text-[#22223B]/80">
                    <div>
                      <div className="overline text-[#6B705C] text-[10px]">Strengths</div>
                      <p className="mt-1 leading-relaxed">{a.strengths}</p>
                    </div>
                    <div>
                      <div className="overline text-[#6B705C] text-[10px]">Decision style</div>
                      <p className="mt-1 leading-relaxed">{a.style}</p>
                    </div>
                    <div>
                      <div className="overline text-[#E0AC29] text-[10px]">Watch out for</div>
                      <p className="mt-1 leading-relaxed text-[#22223B]/70">{a.watchFor}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-14 max-w-3xl mx-auto text-center text-lg text-[#22223B]/75 font-serif-display italic leading-relaxed">
            The most effective leaders don't score highest in one archetype. They know their natural style, recognize their blind spots, and draw on the other five when a decision calls for it.
          </p>
        </div>
      </section>

      {/* 4. HOW IT WORKS — 3 STEPS */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
            <div className="md:col-span-7">
              <div className="overline text-[#0F4C5C]">How it works</div>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mt-4">
                Take. Receive. <em className="italic text-[#1B3654]">Apply.</em>
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="p-8 border-t-2 border-[#1B3654]">
              <div className="overline text-[#B8A26E]">Step 01</div>
              <h3 className="font-serif-display text-2xl mt-3">Take the Genius Leader Archetype™</h3>
              <ul className="mt-5 space-y-3 text-sm text-[#22223B]/80">
                <li className="flex gap-2"><Check size={16} className="text-[#B8A26E] mt-0.5 shrink-0" /> About 6 minutes on any phone or laptop</li>
                <li className="flex gap-2"><Check size={16} className="text-[#B8A26E] mt-0.5 shrink-0" /> Scan a QR code or visit the link</li>
                <li className="flex gap-2"><Check size={16} className="text-[#B8A26E] mt-0.5 shrink-0" /> Answer based on how you <em className="italic">actually</em> decide — not how a leader "should"</li>
              </ul>
            </div>

            {/* Step 2 */}
            <div className="p-8 border-t-2 border-[#1B3654]">
              <div className="overline text-[#B8A26E]">Step 02</div>
              <h3 className="font-serif-display text-2xl mt-3">Receive your results</h3>
              <ul className="mt-5 space-y-3 text-sm text-[#22223B]/80">
                <li className="flex gap-2"><Check size={16} className="text-[#B8A26E] mt-0.5 shrink-0" /> Your primary Genius Leader Archetype</li>
                <li className="flex gap-2"><Check size={16} className="text-[#B8A26E] mt-0.5 shrink-0" /> Your secondary archetype</li>
                <li className="flex gap-2"><Check size={16} className="text-[#B8A26E] mt-0.5 shrink-0" /> Your core decision question</li>
                <li className="flex gap-2"><Check size={16} className="text-[#B8A26E] mt-0.5 shrink-0" /> Strengths, decision style and blind spots</li>
                <li className="flex gap-2"><Check size={16} className="text-[#B8A26E] mt-0.5 shrink-0" /> Your stretch perspective</li>
              </ul>
            </div>

            {/* Step 3 */}
            <div className="p-8 border-t-2 border-[#1B3654]">
              <div className="overline text-[#B8A26E]">Step 03</div>
              <h3 className="font-serif-display text-2xl mt-3">Apply it</h3>
              <div className="mt-5 space-y-4 text-sm text-[#22223B]/80">
                <div>
                  <div className="font-medium text-[#22223B]">Personally</div>
                  <p className="mt-1 leading-relaxed">Before your next big decision, ask: <em className="italic">What moves me toward yes? What does this decision need — and who's in the room?</em></p>
                </div>
                <div>
                  <div className="font-medium text-[#22223B]">With your team</div>
                  <p className="mt-1 leading-relaxed">Compare archetypes, then build decision groups on purpose instead of by habit.</p>
                </div>
                <div>
                  <div className="font-medium text-[#22223B]">Across your organization</div>
                  <p className="mt-1 leading-relaxed">Take the GLAD Score™ (below).</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. GLA → GLAD SCORE™ */}
      <section className="section-pad bg-[#1B3654] text-[#FDFBF7] grain relative overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-6">
            <div className="overline text-[#B8A26E]">Me → We</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mt-4">
              What if you could bring this awareness to <em className="italic text-[#E0AC29]">every team on your campus?</em>
            </h2>
            <div className="mt-8 space-y-6 text-[#FDFBF7]/85 leading-relaxed">
              <p><strong className="text-[#E0AC29]">Genius Leader Archetype™</strong> = How I decide. One person's natural decision driver.</p>
              <p><strong className="text-[#E0AC29]">GLAD Score™</strong> = How we decide. The combined decision pattern of a leadership team, department or whole organization.</p>
              <p className="text-[#FDFBF7]/70 italic">You don't rebuild your leadership team every time a decision comes up. You need to know who is already in the room — and who isn't.</p>
            </div>
            <div className="mt-8">
              <a
                href={BOOK_CALL_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="gla-book-glad"
                className="btn-primary bg-[#E0AC29] border-[#E0AC29] text-[#1B3654] hover:bg-[#c9991f]"
              >
                Book a call about your team's GLAD Score™ <ArrowUpRight size={16} />
              </a>
            </div>
          </div>

          <div className="md:col-span-6">
            <div className="overline text-[#B8A26E] mb-4">The GLAD Score™ reveals</div>
            <ul className="space-y-4">
              {[
                { k: "Dominant drivers", v: "The perspectives your team leans on most" },
                { k: "Missing perspectives", v: "The archetypes that are underrepresented or absent" },
                { k: "Decision blind spots", v: "What your team is likely to overlook as a group" },
                { k: "Speed and stall points", v: "Where decisions speed up — and where they get stuck" },
                { k: "Team dynamics", v: "Who aligns easily and where productive tension lives" },
              ].map((r) => (
                <li key={r.k} className="p-5 rounded-lg border border-white/10 bg-white/[0.03]">
                  <div className="font-serif-display text-lg text-[#E0AC29]">{r.k}</div>
                  <div className="text-sm text-[#FDFBF7]/80 mt-1">{r.v}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 6. HOW ARCHETYPES WORK TOGETHER */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <div className="overline text-[#0F4C5C]">How archetypes work together</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mt-4">
              The person who slows you down may be exactly the perspective <em className="italic text-[#1B3654]">the decision requires.</em>
            </h2>
          </div>

          <div className="mt-14">
            <div className="overline text-[#B8A26E] mb-6">Natural allies · easy agreement, shared blind spot</div>
            <div className="grid md:grid-cols-3 gap-6">
              {allies.map((a) => (
                <div key={a.pair} className="card-soft p-8">
                  <div className="font-serif-display text-lg text-[#1B3654]">{a.pair}</div>
                  <div className="mt-3 font-serif-display italic text-xl text-[#B8A26E]">{a.tag}</div>
                  <p className="mt-4 text-sm text-[#22223B]/75 leading-relaxed">{a.body}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-14">
            <div className="overline text-[#B8A26E] mb-6">Productive tensions · friction that covers each other's blind spots</div>
            <div className="grid md:grid-cols-3 gap-6">
              {tensions.map((t) => (
                <div key={t.pair} className="p-8 rounded-lg border-2 border-dashed border-[#1B3654]/25 bg-[#FDFBF7]">
                  <div className="font-serif-display text-lg text-[#1B3654]">{t.pair}</div>
                  <div className="mt-3 font-serif-display italic text-xl text-[#0F4C5C]">{t.line}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 7. WHO IT'S FOR */}
      <section className="section-pad bg-[#F0EDE6]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <div className="overline text-[#0F4C5C]">Who it's for</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mt-4">
              Where the Genius Leader Archetype™ and GLAD Score™ <em className="italic text-[#1B3654]">actually help.</em>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {audiences.map((a) => (
              <div key={a.title} className="card-soft p-8">
                <h3 className="font-serif-display text-2xl">{a.title}</h3>
                <ul className="mt-5 space-y-3 text-[15px] text-[#22223B]/80">
                  {a.points.map((p) => (
                    <li key={p} className="flex gap-3">
                      <ChevronRight size={16} className="text-[#B8A26E] mt-1 shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. WAYS TO EXPERIENCE */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
            <div className="md:col-span-7">
              <div className="overline text-[#0F4C5C]">Four ways to experience the Genius Leader Archetype™</div>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mt-4">
                From an individual insight — <em className="italic text-[#1B3654]">to a campus-wide shift.</em>
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {experiences.map((e) => (
              <div key={e.title} className="card-soft p-8 flex flex-col">
                <div className="overline text-[#B8A26E]">{e.tag}</div>
                <h3 className="font-serif-display text-2xl mt-3 leading-snug">{e.title}</h3>
                <p className="mt-4 text-[15px] text-[#22223B]/75 leading-relaxed flex-1">{e.body}</p>
                {e.external ? (
                  <a
                    href={e.href}
                    target="_blank"
                    rel="noreferrer"
                    className="link-underline mt-6 inline-flex items-center gap-1 self-start text-sm"
                  >
                    {e.cta} <ArrowUpRight size={14} />
                  </a>
                ) : (
                  <Link to={e.href} className="link-underline mt-6 inline-flex items-center gap-1 self-start text-sm">
                    {e.cta} <ArrowUpRight size={14} />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. COMPARISON TABLE */}
      <section className="section-pad bg-[#F0EDE6]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
              <div className="overline text-[#0F4C5C]">Genius Leader Archetype™ vs. GLAD Score™</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mt-4">
              One decision. <em className="italic text-[#1B3654]">Two levels of insight.</em>
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="card-soft p-8 md:p-10">
              <div className="overline text-[#B8A26E]">Individual</div>
              <h3 className="font-serif-display text-3xl mt-3">Genius Leader Archetype™</h3>
              <dl className="mt-8 space-y-5 text-[15px]">
                <div>
                  <dt className="text-[#6B705C] text-xs uppercase tracking-widest">Focus</dt>
                  <dd className="mt-1 text-[#22223B]">The individual</dd>
                </div>
                <div>
                  <dt className="text-[#6B705C] text-xs uppercase tracking-widest">Answers</dt>
                  <dd className="mt-1 font-serif-display italic text-[#1B3654]">"How do I decide?"</dd>
                </div>
                <div>
                  <dt className="text-[#6B705C] text-xs uppercase tracking-widest">Reveals</dt>
                  <dd className="mt-1 text-[#22223B]">Your primary driver, strengths and blind spots</dd>
                </div>
                <div>
                  <dt className="text-[#6B705C] text-xs uppercase tracking-widest">Best for</dt>
                  <dd className="mt-1 text-[#22223B]">Self-awareness and personal growth</dd>
                </div>
                <div>
                  <dt className="text-[#6B705C] text-xs uppercase tracking-widest">Time</dt>
                  <dd className="mt-1 text-[#22223B]">About 6 minutes</dd>
                </div>
              </dl>
              <a
                href={GLA_ASSESSMENT_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-primary mt-8 text-sm bg-[#1B3654] border-[#1B3654]"
              >
                Take the Genius Leader Archetype™ <ArrowUpRight size={14} />
              </a>
            </div>

            <div className="p-8 md:p-10 rounded-lg bg-[#1B3654] text-[#FDFBF7] border-t-4 border-[#E0AC29]">
              <div className="overline text-[#B8A26E]">Team &amp; Organization</div>
              <h3 className="font-serif-display text-3xl mt-3">GLAD Score™</h3>
              <dl className="mt-8 space-y-5 text-[15px]">
                <div>
                  <dt className="text-[#FDFBF7]/50 text-xs uppercase tracking-widest">Focus</dt>
                  <dd className="mt-1 text-[#FDFBF7]">The team or organization</dd>
                </div>
                <div>
                  <dt className="text-[#FDFBF7]/50 text-xs uppercase tracking-widest">Answers</dt>
                  <dd className="mt-1 font-serif-display italic text-[#E0AC29]">"How do we decide?"</dd>
                </div>
                <div>
                  <dt className="text-[#FDFBF7]/50 text-xs uppercase tracking-widest">Reveals</dt>
                  <dd className="mt-1 text-[#FDFBF7]">Dominant and missing drivers, team blind spots, stall points</dd>
                </div>
                <div>
                  <dt className="text-[#FDFBF7]/50 text-xs uppercase tracking-widest">Best for</dt>
                  <dd className="mt-1 text-[#FDFBF7]">Leadership teams, departments, whole campuses</dd>
                </div>
                <div>
                  <dt className="text-[#FDFBF7]/50 text-xs uppercase tracking-widest">Next step</dt>
                  <dd className="mt-1 text-[#FDFBF7]">Book a call</dd>
                </div>
              </dl>
              <a
                href={BOOK_CALL_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-primary mt-8 text-sm bg-[#E0AC29] border-[#E0AC29] text-[#1B3654] hover:bg-[#c9991f]"
              >
                Book a Call <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 10. FAQ */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <div className="overline text-[#0F4C5C]">Frequently asked</div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-tight mt-4">Answers before you ask.</h2>
          <div className="mt-12 divide-y divide-[#1B3654]/15">
            {faqs.map((f, i) => (
              <details key={i} data-testid={`gla-faq-${i}`} className="group py-6">
                <summary className="cursor-pointer flex items-start justify-between gap-6 list-none">
                  <span className="font-serif-display text-xl md:text-2xl leading-snug">{f.q}</span>
                  <span className="text-[#1B3654] text-2xl leading-none mt-1 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-[#22223B]/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* 11. FINAL CTA BAND */}
      <section className="section-pad bg-[#1B3654] text-[#FDFBF7] grain relative overflow-hidden">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 text-center">
          <div className="overline text-[#B8A26E]">Make great decisions faster</div>
          <h2 className="font-serif-display text-4xl md:text-6xl mt-4 leading-tight">
            Start with how you decide. <em className="italic text-[#E0AC29]">Then discover how your team decides together.</em>
          </h2>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <a
              href={GLA_ASSESSMENT_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="gla-final-take"
              className="btn-primary bg-[#E0AC29] border-[#E0AC29] text-[#1B3654] hover:bg-[#c9991f]"
            >
              Take the Genius Leader Archetype™ <ArrowUpRight size={16} />
            </a>
            <a
              href={BOOK_CALL_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="gla-final-book"
              className="btn-secondary !text-[#FDFBF7] !border-[#FDFBF7]/60 hover:!bg-white/10"
            >
              Book a Call
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
